import { FEATURE_KEYS, MODULE_KEYS, OPERATION_IDS } from '../routes/ids.js';
import { MARKET_PRICE_ROUTE_IDS } from '../routes/market-price-route-ids.js';
import { MARKET_PRICES_LIST_QUERY_ID, MARKET_PRICES_TABLE_ID, } from '../data/reference-data-resource.registry.js';
import { importPageAction } from './table-registry.js';
const tk = (value) => value;
const telemetry = (value) => value;
const query = (value) => value;
function metadata(id, module, feature) {
    return {
        id,
        schemaVersion: 1,
        module,
        feature,
        privacyClassification: 'CONFIDENTIAL',
        telemetryId: telemetry(`experience.table.${id}`),
        accessibilityLabelKey: tk('a11y.table.marketPrices'),
        availability: 'AVAILABLE',
        owner: 'market-data',
    };
}
function column(id, overrides = {}) {
    return {
        ...metadata(id, MODULE_KEYS.MARKET_DATA, FEATURE_KEYS.DATA_MARKET_PRICES),
        labelKey: tk(`data:marketPrices.columns.${id}`),
        field: id,
        fieldType: 'text',
        sortable: false,
        filterable: false,
        searchable: false,
        groupable: false,
        hideable: true,
        defaultVisible: true,
        requiredVisible: false,
        resizable: true,
        reorderable: true,
        pinnable: false,
        defaultWidth: 140,
        minWidth: 90,
        maxWidth: 320,
        align: 'left',
        nullDisplayBehavior: 'dash',
        ...overrides,
    };
}
const decimalPriceColumn = (id, overrides = {}) => column(id, {
    fieldType: 'decimal',
    align: 'right',
    defaultWidth: 118,
    ...overrides,
});
const correctAction = {
    ...metadata('action.marketPrices.correct', MODULE_KEYS.MARKET_DATA, FEATURE_KEYS.DATA_MARKET_PRICES),
    operationId: OPERATION_IDS.MARKET_DATA_UPDATE,
    labelKey: tk('data:marketPrices.actions.correct'),
    iconKey: 'edit',
    kind: 'navigate',
    intent: 'primary',
    idempotencyRequired: false,
    successBehavior: 'NAVIGATE_TO',
    targetRouteId: MARKET_PRICE_ROUTE_IDS.EDIT,
    successNavigateTo: '/data/market-prices/:id/edit',
    errorBehavior: 'SHOW_INLINE_ERROR',
    order: 10,
    group: 'navigation',
};
export const MARKET_PRICES_TABLE = Object.freeze({
    ...metadata(MARKET_PRICES_TABLE_ID, MODULE_KEYS.MARKET_DATA, FEATURE_KEYS.DATA_MARKET_PRICES),
    titleKey: tk('card.marketPrices.title'),
    descriptionKey: tk('data:marketPrices.description'),
    dataSourceId: query(MARKET_PRICES_LIST_QUERY_ID),
    rowIdField: 'id',
    columns: [
        column('tradedDate', {
            fieldType: 'date',
            sortable: true,
            filterable: true,
            requiredVisible: true,
            defaultWidth: 130,
        }),
        column('instrumentSymbol', {
            sortable: true,
            filterable: true,
            searchable: true,
            requiredVisible: true,
            defaultWidth: 135,
        }),
        column('instrumentName', { searchable: true, defaultWidth: 210 }),
        decimalPriceColumn('open'),
        decimalPriceColumn('high'),
        decimalPriceColumn('low'),
        decimalPriceColumn('close', {
            sortable: true,
            requiredVisible: true,
            defaultWidth: 125,
        }),
        decimalPriceColumn('last', { defaultVisible: false }),
        decimalPriceColumn('volume', { defaultWidth: 135 }),
        decimalPriceColumn('turnover', { defaultVisible: false, defaultWidth: 145 }),
        column('tradeCount', {
            fieldType: 'number',
            align: 'right',
            defaultVisible: false,
            defaultWidth: 120,
        }),
        column('observationKind', {
            fieldType: 'status',
            defaultVisible: false,
            defaultWidth: 155,
        }),
        column('priceType', {
            fieldType: 'status',
            sortable: true,
            filterable: true,
            defaultVisible: false,
            defaultWidth: 130,
        }),
        decimalPriceColumn('canonicalPrice', {
            labelKey: tk('data:marketPrices.columns.canonicalPrice'),
            defaultVisible: false,
            defaultWidth: 150,
        }),
        decimalPriceColumn('price', {
            defaultVisible: false,
            sortable: true,
            defaultWidth: 130,
        }),
        column('currency', { filterable: true, defaultWidth: 95, defaultVisible: false }),
        column('exchange', { filterable: true, defaultWidth: 105 }),
        column('source', { filterable: true, defaultWidth: 135 }),
        column('freshness', { fieldType: 'status', defaultWidth: 120 }),
        column('asOf', {
            fieldType: 'datetime',
            defaultVisible: false,
            defaultWidth: 180,
        }),
        column('createdAt', {
            fieldType: 'datetime',
            defaultVisible: false,
            defaultWidth: 180,
        }),
        column('updatedAt', {
            fieldType: 'datetime',
            sortable: true,
            defaultVisible: false,
            defaultWidth: 180,
        }),
        column('sourceEntity', { defaultVisible: false, defaultWidth: 140 }),
        column('sourceRecordId', { defaultVisible: false, defaultWidth: 240 }),
        column('asOfDate', {
            fieldType: 'datetime',
            defaultVisible: false,
            defaultWidth: 180,
        }),
        column('effectiveTimestamp', {
            fieldType: 'datetime',
            defaultVisible: false,
            defaultWidth: 180,
        }),
        column('restatementState', {
            fieldType: 'status',
            defaultVisible: false,
            defaultWidth: 150,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50, 100] },
    defaultSort: { field: 'tradedDate', direction: 'desc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: {
        sorting: true,
        filtering: true,
        globalSearch: true,
        grouping: false,
        aggregation: false,
        pagination: true,
        selection: false,
        columnVisibility: true,
        columnResizing: true,
        columnReordering: true,
        columnPinning: false,
        savedViews: false,
        export: true,
        refresh: true,
        density: true,
        create: true,
    },
    responsive: {
        mode: 'scroll',
        priorityColumns: ['tradedDate', 'instrumentSymbol', 'close', 'volume'],
    },
    pageActions: [
        importPageAction('action.data.marketPrices.import', MODULE_KEYS.MARKET_DATA, FEATURE_KEYS.DATA_MARKET_PRICES, 'CONFIDENTIAL', 'MARKET_DATA'),
    ],
    rowActions: [correctAction],
    toolbar: {
        showResultCount: true,
        showGlobalSearch: true,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showDensity: true,
        showRefresh: true,
        showExport: true,
        showCreateAction: true,
    },
    transfer: {
        import: {
            definitionId: 'MARKET_DATA',
            autoDetection: true,
            templatesSupported: true,
        },
        export: {
            formats: ['csv', 'xlsx'],
            scopes: ['all', 'filtered', 'selected'],
            asyncThreshold: 50_000,
        },
    },
    rowActivationAction: {
        ...metadata('action.marketPrices.view', MODULE_KEYS.MARKET_DATA, FEATURE_KEYS.DATA_MARKET_PRICES),
        operationId: OPERATION_IDS.MARKET_DATA_GET,
        labelKey: tk('action.view'),
        kind: 'navigate',
        intent: 'secondary',
        targetRouteId: 'data-market-price-detail',
        successNavigateTo: '/data/market-prices/:id',
        errorBehavior: 'SHOW_INLINE_ERROR',
        order: 20,
        group: 'navigation',
        idempotencyRequired: false,
        successBehavior: 'NO_OP',
    },
});
export const MARKET_DATA_TABLE_REGISTRY = Object.freeze({
    [MARKET_PRICES_TABLE.id]: MARKET_PRICES_TABLE,
});
